/** Confirmed contracts, with derived payment status (§4.5). */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Receipt, Search } from 'lucide-react';
import { api } from '../../lib/api';
import { peso, shortDate } from '../../lib/format';
import {
  Button,
  Card,
  EmptyState,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  filterClass,
  type ChipTone,
} from '../../components/ui';

type PaymentStatus =
  | 'UNBILLED'
  | 'PARTIALLY_BILLED'
  | 'FULLY_BILLED_UNPAID'
  | 'PARTIALLY_PAID'
  | 'FULLY_PAID';

interface SalesOrderRow {
  id: string;
  code: string;
  projectName: string;
  confirmedAt: string;
  totalContractPrice: string;
  billedTotal: string;
  collectedTotal: string;
  outstanding: string;
  contractBalance: string;
  awaitingConfirmation: string;
  paymentStatus: PaymentStatus;
  mrcAmountVatEx: string | null;
  customer: { id: string; name: string };
  project: { id: string; code: string; status: string } | null;
}

export const PAYMENT_LABELS: Record<PaymentStatus, { label: string; tone: ChipTone }> = {
  UNBILLED: { label: 'Unbilled', tone: 'neutral' },
  PARTIALLY_BILLED: { label: 'Partially Billed', tone: 'info' },
  FULLY_BILLED_UNPAID: { label: 'Fully Billed – Unpaid', tone: 'warning' },
  PARTIALLY_PAID: { label: 'Partially Paid', tone: 'warning' },
  FULLY_PAID: { label: 'Fully Paid', tone: 'ok' },
};

export const PROJECT_TONES: Record<string, ChipTone> = {
  PENDING: 'neutral',
  ONGOING: 'info',
  COMPLETED: 'ok',
  CANCELLED: 'neutral',
};

export function SalesOrdersPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [projectStatus, setProjectStatus] = useState('');
  const [page, setPage] = useState(1);

  const params = new URLSearchParams({ page: String(page), pageSize: '25' });
  if (search) params.set('q', search);
  if (paymentStatus) params.set('paymentStatus', paymentStatus);
  if (projectStatus) params.set('projectStatus', projectStatus);

  const orders = useQuery({
    queryKey: ['sales-orders', params.toString()],
    queryFn: () =>
      api<{ data: SalesOrderRow[]; total: number; pageSize: number }>(
        `/sales-orders?${params.toString()}`,
      ),
  });

  const total = orders.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / (orders.data?.pageSize ?? 25)));

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header>
        <h1 className="text-xl font-bold text-navy dark:text-white">Sales orders</h1>
        <p className="text-sm text-slate-500">
          Every confirmed deal, with what has been billed and collected against it.
        </p>
      </header>

      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative min-w-40 flex-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <input
              className={`${filterClass} w-full pl-8`}
              placeholder="Search order, customer or project"
              value={search}
              onChange={(event) => {
                setPage(1);
                setSearch(event.target.value);
              }}
            />
          </div>
          <select
            className={`${filterClass} flex-1 sm:flex-none sm:w-44`}
            value={paymentStatus}
            onChange={(event) => {
              setPage(1);
              setPaymentStatus(event.target.value);
            }}
          >
            <option value="">All payment statuses</option>
            {Object.entries(PAYMENT_LABELS).map(([value, meta]) => (
              <option key={value} value={value}>
                {meta.label}
              </option>
            ))}
          </select>
          <select
            className={`${filterClass} flex-1 sm:flex-none sm:w-36`}
            value={projectStatus}
            onChange={(event) => {
              setPage(1);
              setProjectStatus(event.target.value);
            }}
          >
            <option value="">All project statuses</option>
            <option value="PENDING">Pending</option>
            <option value="ONGOING">Ongoing</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        {orders.isLoading && <Skeleton className="h-64 w-full" />}

        {orders.data?.data.length === 0 && (
          <EmptyState
            title="No sales orders yet"
            description="Confirm a lead and its sales order appears here."
          />
        )}

        {(orders.data?.data.length ?? 0) > 0 && (
          <>
            <TableWrap>
              <thead>
                <tr>
                  <Th>Order</Th>
                  <Th>Customer</Th>
                  <Th align="right">Contract (VAT-inc)</Th>
                  <Th align="right">Billed</Th>
                  <Th align="right">Collected</Th>
                  <Th align="right">Balance</Th>
                  <Th>Payment</Th>
                  <Th>Project</Th>
                  <Th>Confirmed</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {orders.data?.data.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <Td>
                      <Link
                        to={`/sales-orders/${row.id}`}
                        className="font-medium text-navy hover:underline dark:text-white"
                      >
                        {row.projectName}
                      </Link>
                      <p className="font-mono text-xs text-slate-500">
                        {row.code}
                        {row.mrcAmountVatEx ? ' · recurring' : ''}
                      </p>
                    </Td>
                    <Td>{row.customer.name}</Td>
                    <Td align="right" className="tabular">
                      {peso(row.totalContractPrice)}
                    </Td>
                    <Td align="right" className="tabular">
                      {peso(row.billedTotal)}
                    </Td>
                    <Td align="right" className="tabular">
                      {peso(row.collectedTotal)}
                    </Td>
                    <Td align="right" className="tabular font-semibold">
                      {peso(row.contractBalance)}
                      {Number(row.outstanding) > 0 && (
                        <span className="block text-xs font-normal text-slate-500">
                          {peso(row.outstanding)} due now
                        </span>
                      )}
                      {Number(row.awaitingConfirmation) > 0 && (
                        <span className="block text-xs font-normal text-amber-600 dark:text-amber-400">
                          {peso(row.awaitingConfirmation)} awaiting confirmation
                        </span>
                      )}
                    </Td>
                    <Td>
                      <StatusChip tone={PAYMENT_LABELS[row.paymentStatus].tone}>
                        {PAYMENT_LABELS[row.paymentStatus].label}
                      </StatusChip>
                    </Td>
                    <Td>
                      {row.project ? (
                        <Link to={`/projects/${row.project.id}`} className="hover:underline">
                          <StatusChip tone={PROJECT_TONES[row.project.status] ?? 'neutral'}>
                            {row.project.status.charAt(0) +
                              row.project.status.slice(1).toLowerCase()}
                          </StatusChip>
                        </Link>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td>{shortDate(row.confirmedAt)}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>

            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span className="inline-flex items-center gap-2">
                <Receipt aria-hidden="true" className="h-4 w-4" />
                {total} order{total === 1 ? '' : 's'}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((value) => value - 1)}
                >
                  Previous
                </Button>
                <span className="self-center">
                  {page} / {lastPage}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page >= lastPage}
                  onClick={() => setPage((value) => value + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
